import { useEffect, useMemo, useState } from 'react';
import { App, Button, Drawer, Radio, Select, Space, Typography } from 'antd';
import type { ListIAMUserGroupsRequest } from '@/api/iam.api';
import { useIamUserGroupList } from '@/pages/iam/composables/useIamGroups';
import {
  usePolicyGrantRecords,
  useSavePolicyGrant,
} from '@/pages/iam/composables/useGrantPolicyData';
import { useIamUserList } from '@/pages/iam/composables/useIamUsers';
import type { ListIAMUsersRequest } from '@/types/iam.types';
import './GrantPolicyDrawer.css';

type IamPrincipalType = 'USER' | 'GROUP';

const ALL_USERS_REQUEST: ListIAMUsersRequest = {
  page: 1,
  limit: -1,
  order_by: ['-created_at'],
  name: '',
  display_name: '',
};

const ALL_USER_GROUPS_REQUEST: ListIAMUserGroupsRequest = {
  page: 1,
  limit: -1,
  name: '',
  display_name: '',
  keyword: '',
};

interface Props {
  open: boolean;
  policy?: { id: string; name: string };
  onClose: () => void;
}

export default function GrantPolicyDrawer({ open, policy, onClose }: Props) {
  const { message } = App.useApp();
  const [principalType, setPrincipalType] = useState<IamPrincipalType>('USER');
  const [principalIds, setPrincipalIds] = useState<string[]>([]);
  const policyId = policy?.id;
  const usersQuery = useIamUserList(ALL_USERS_REQUEST, open && principalType === 'USER');
  const userGroupsQuery = useIamUserGroupList(
    ALL_USER_GROUPS_REQUEST,
    open && principalType === 'GROUP'
  );
  const grantRecordsQuery = usePolicyGrantRecords(policyId, open);
  const savePolicyGrant = useSavePolicyGrant();

  useEffect(() => {
    if (!open || !policyId) return;
    setPrincipalIds([]);
    setPrincipalType('USER');
  }, [open, policyId]);

  const options = useMemo(() => {
    const existing = grantRecordsQuery.data?.data.records ?? [];
    if (principalType === 'USER') {
      return (usersQuery.data?.items ?? []).map((user) => ({
        value: user.id,
        label: `${user.name}（${user.id}）`,
        disabled: existing.some(
          (grant) => grant.identity_type === 'user' && grant.identity === user.id
        ),
      }));
    }

    return (userGroupsQuery.data?.items ?? []).map((group) => ({
      value: group.id,
      label: `${group.display_name || group.name}（${group.id}）`,
      disabled: existing.some(
        (grant) => grant.identity_type === 'user_group' && grant.identity === group.id
      ),
    }));
  }, [grantRecordsQuery.data, principalType, userGroupsQuery.data, usersQuery.data]);

  const submit = async () => {
    if (!policy || !principalIds.length) return;
    try {
      await savePolicyGrant.mutateAsync({
        policy_ids: [policy.id],
        identities: principalIds.map((identity) => ({
          identity_type: principalType === 'USER' ? 'user' : 'user_group',
          identity,
        })),
      });
      message.success(`已向 ${principalIds.length} 个主体授权`);
      onClose();
    } catch {
      // 请求错误由全局请求层统一提示。
    }
  };

  const loading =
    grantRecordsQuery.isFetching ||
    (principalType === 'USER' ? usersQuery.isFetching : userGroupsQuery.isFetching);

  return (
    <Drawer
      title={`授权策略 · ${policy?.name || ''}`}
      width={640}
      open={open}
      onClose={onClose}
      footer={
        <div className="iam-grant-policy-drawer-footer">
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            disabled={!principalIds.length}
            loading={savePolicyGrant.isPending}
            onClick={submit}
          >
            确认授权
          </Button>
        </div>
      }
    >
      <Space direction="vertical" size={20} className="iam-grant-policy-drawer-body">
        <div>
          <Typography.Title level={5}>主体类别</Typography.Title>
          <Radio.Group
            value={principalType}
            onChange={(event) => {
              setPrincipalType(event.target.value);
              setPrincipalIds([]);
            }}
            options={[
              { value: 'USER', label: '用户' },
              { value: 'GROUP', label: '用户组' },
            ]}
          />
        </div>
        <div>
          <Typography.Title level={5}>
            选择{principalType === 'USER' ? '用户' : '用户组'}
          </Typography.Title>
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            placeholder="选择一个或多个授权主体"
            value={principalIds}
            onChange={setPrincipalIds}
            options={options}
            loading={loading}
            virtual
          />
        </div>
        <Typography.Text type="secondary">
          {principalType === 'USER'
            ? `预计影响 ${principalIds.length} 名用户。`
            : `将向 ${principalIds.length} 个用户组授权。`}
          直接授权与用户组继承授权共同计算，Deny 优先。
        </Typography.Text>
      </Space>
    </Drawer>
  );
}
